import React, { useState, useEffect } from 'react';
import ConfirmationModal from './ConfirmationModal';

const UserManagementPopup = ({ 
  user, 
  availableReports, 
  onClose, 
  onUpdateLicense, 
  onDeleteUser, 
  onAssignReports, 
  onRemoveReports,
  licenseInfo,
  loading,
  isSuspended = false,
  originalAdminId = null,
  originalAdminEmail = null,
  adminList = []
}) => {
  const [activeTab, setActiveTab] = useState('details');
  const [selectedReports, setSelectedReports] = useState([]);
  const [selectedAssignedReports, setSelectedAssignedReports] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNoLicenseAlert, setShowNoLicenseAlert] = useState(false);
  const [showNoReportsSelectedAlert, setShowNoReportsSelectedAlert] = useState(false);
  const [alertType, setAlertType] = useState(''); // 'assign' or 'remove'

  const userName = user.fullName || user.FullName || 'No Name Provided';
  const userEmail = user.email || user.Email;
  const userId = user.userID || user.UserID;
  const hasLicense = user.hasActiveLicense || user.HasActiveLicense;
  const assignedReports = user.assignedReports || user.AssignedReports || [];

  // Get unassigned reports (reports not assigned to this user)
  const unassignedReports = availableReports.filter(report => 
    !assignedReports.some(assigned => 
      (assigned.reportID || assigned.ReportID) === (report.reportID || report.ReportID)
    )
  );

  const handleLicenseToggle = async () => {
    const newLicenseStatus = !hasLicense;
    
    if (newLicenseStatus && licenseInfo.available <= 0) {
      setShowNoLicenseAlert(true);
      return;
    }

    await onUpdateLicense(user, newLicenseStatus);
  };

  const handleDeleteUser = () => {
    // Prevent deleting subscription owner
    const adminId = originalAdminId != null ? String(originalAdminId) : null;
    const adminEmail = originalAdminEmail ? String(originalAdminEmail).trim().toLowerCase() : null;
    const thisUserId = user.userID || user.UserID;
    const thisUserEmail = user.email || user.Email || '';

    const normThisUserId = thisUserId != null ? String(thisUserId) : null;
    const normThisUserEmail = thisUserEmail.trim().toLowerCase();

    if ((adminId && normThisUserId === adminId) || (adminEmail && normThisUserEmail === adminEmail)) {
      // Show an alert modal instead of allowing delete
      alert('You cannot delete the subscription owner (beneficiary).');
      return;
    }

    // Prevent deleting users who are also admins
    const isAdmin = adminList.some(admin => {
      const adminEmailNorm = (admin.email || admin.Email || '').trim().toLowerCase();
      const adminIdNorm = admin.adminID || admin.AdminID;
      return adminEmailNorm === normThisUserEmail || String(adminIdNorm) === normThisUserId;
    });

    if (isAdmin) {
      alert('You cannot delete a user who is also an administrator. Remove them from the Admins tab first.');
      return;
    }

    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    setShowDeleteConfirm(false);
    await onDeleteUser(user);
    onClose();
  };

  const handleAssignReports = async () => {
    if (selectedReports.length === 0) {
      setAlertType('assign');
      setShowNoReportsSelectedAlert(true);
      return;
    }

    await onAssignReports(userId, selectedReports);
    setSelectedReports([]);
    setActiveTab('details'); // Switch back to details after assignment
  };

  const handleRemoveReports = async () => {
    if (selectedAssignedReports.length === 0) {
      setAlertType('remove');
      setShowNoReportsSelectedAlert(true);
      return;
    }

    await onRemoveReports(userId, selectedAssignedReports);
    setSelectedAssignedReports([]);
    setActiveTab('details'); // Switch back to details after removal
  };

  const toggleReportSelection = (reportId, isAssigned = false) => {
    if (isAssigned) {
      setSelectedAssignedReports(prev => 
        prev.includes(reportId) 
          ? prev.filter(id => id !== reportId)
          : [...prev, reportId]
      );
    } else {
      setSelectedReports(prev => 
        prev.includes(reportId) 
          ? prev.filter(id => id !== reportId)
          : [...prev, reportId]
      );
    }
  };

  return (
    <div className="popup-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div className="popup-content" style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa'
        }}>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#495057' }}>{userName}</h3>
            <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9rem' }}>{userEmail}</p>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa'
        }}>
          {[
            { id: 'details', label: 'Details' },
            { id: 'assign', label: 'Assign Reports' },
            { id: 'remove', label: 'Unassign Reports' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
                style={{
                padding: '1rem 1.5rem',
                border: 'none',
                backgroundColor: activeTab === tab.id ? '#3A6A92' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#495057',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                transition: 'all 0.3s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          padding: '1.5rem',
          maxHeight: '60vh',
          overflowY: 'auto'
        }}>
          {/* Subscription Suspended Alert */}
          {isSuspended && (
            <div style={{
              background: '#fff3cd',
              border: '2px solid #ffc107',
              padding: '1rem',
              marginBottom: '1.5rem',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16" style={{ color: '#856404', flexShrink: 0 }}>
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
              </svg>
              <div>
                <strong style={{ color: '#856404' }}>Subscription Suspended</strong>
                <p style={{ margin: '0.25rem 0 0 0', color: '#856404', fontSize: '0.9rem' }}>
                  User management functions are currently disabled. Users cannot access reports.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="user-details">
              {/* License Status */}
              <div style={{ marginBottom: '2rem' }}>
                <h4>License Status</h4>
                <div style={{
                  padding: '1rem',
                  backgroundColor: hasLicense ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${hasLicense ? '#c3e6cb' : '#f5c6cb'}`,
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong>Current Status:</strong>
                      <span style={{
                        marginLeft: '0.5rem',
                        padding: '0.2rem 0.6rem',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        background: hasLicense ? '#28a745' : '#6c757d',
                        color: 'white'
                      }}>
                        {hasLicense ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <button
                      className={`btn ${hasLicense ? 'btn-warning' : 'btn-success'}`}
                      onClick={handleLicenseToggle}
                      disabled={loading || isSuspended || (!hasLicense && licenseInfo.available <= 0)}
                      style={{ fontSize: '0.9rem' }}
                      title={isSuspended ? 'Disabled - Subscription Suspended' : ''}
                    >
                      {hasLicense ? 'Deactivate License' : 'Activate License'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Assigned Reports */}
              <div style={{ marginBottom: '2rem' }}>
                <h4>Assigned Reports ({assignedReports.length})</h4>
                {assignedReports.length === 0 ? (
                  <p style={{ color: '#6c757d', fontStyle: 'italic' }}>No reports assigned to this user.</p>
                ) : (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {assignedReports.map((report, index) => {
                      const reportName = report.reportName || report.ReportName;
                      const uploadDate = report.uploadDate || report.UploadDate;
                      return (
                        <div key={index} style={{
                          padding: '0.75rem',
                          backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{ fontWeight: '600' }}>{reportName}</div>
                          <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                            Uploaded: {new Date(uploadDate).toLocaleDateString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Delete User */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '8px'
              }}>
                <h4 style={{ color: '#721c24' }}>Delete User</h4>
                <p style={{ margin: '0.5rem 0', color: '#721c24', fontSize: '0.9rem' }}>
                  Permanently remove this user from the system. This action cannot be undone.
                </p>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteUser}
                  disabled={loading || isSuspended}
                  title={isSuspended ? 'Disabled - Subscription Suspended' : ''}
                >
                  Delete User
                </button>
              </div>
            </div>
          )}

          {activeTab === 'assign' && (
            <div className="assign-reports">
              <h4>Assign Reports to {userName}</h4>
              <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                Select reports to assign to this user. User must have an active license to access reports.
              </p>

              {unassignedReports.length === 0 ? (
                <p style={{ color: '#6c757d', fontStyle: 'italic' }}>All available reports are already assigned to this user.</p>
              ) : (
                <>
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    {unassignedReports.map((report, index) => {
                      const reportId = report.reportID || report.ReportID;
                      const reportName = report.reportName || report.ReportName;
                      const uploadDate = report.uploadDate || report.UploadDate;
                      const uploadedBy = report.uploadedByAdminName || report.UploadedByAdminName;
                      const isSelected = selectedReports.includes(reportId);

                      return (
                        <div
                          key={reportId}
                          onClick={() => toggleReportSelection(reportId)}
                          style={{
                            padding: '1rem',
                            backgroundColor: isSelected ? '#e3f2fd' : (index % 2 === 0 ? '#f8f9fa' : 'white'),
                            border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}} // Handled by div click
                              style={{ marginRight: '0.5rem' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{reportName}</div>
                              <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                Uploaded: {new Date(uploadDate).toLocaleDateString()} by {uploadedBy}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <button
                      className="btn btn-primary"
                      onClick={handleAssignReports}
                      disabled={loading || isSuspended || selectedReports.length === 0}
                      title={isSuspended ? 'Disabled - Subscription Suspended' : ''}
                    >
                      Assign {selectedReports.length} Selected Report(s)
                    </button>
                    <span style={{ color: '#6c757d' }}>
                      {selectedReports.length} of {unassignedReports.length} reports selected
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'remove' && (
            <div className="remove-reports">
              <h4>Remove Reports from {userName}</h4>
              <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                Select reports to remove from this user. The reports will remain available for reassignment.
              </p>

              {assignedReports.length === 0 ? (
                <p style={{ color: '#6c757d', fontStyle: 'italic' }}>No reports are currently assigned to this user.</p>
              ) : (
                <>
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    {assignedReports.map((report, index) => {
                      const reportId = report.reportID || report.ReportID;
                      const reportName = report.reportName || report.ReportName;
                      const uploadDate = report.uploadDate || report.UploadDate;
                      const isSelected = selectedAssignedReports.includes(reportId);

                      return (
                        <div
                          key={reportId}
                          onClick={() => toggleReportSelection(reportId, true)}
                          style={{
                            padding: '1rem',
                            backgroundColor: isSelected ? '#ffeaa7' : (index % 2 === 0 ? '#f8f9fa' : 'white'),
                            border: isSelected ? '2px solid #f39c12' : '1px solid #dee2e6',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}} // Handled by div click
                              style={{ marginRight: '0.5rem' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{reportName}</div>
                              <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                Assigned: {new Date(uploadDate).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <button
                      className="btn btn-warning"
                      onClick={handleRemoveReports}
                      disabled={loading || isSuspended || selectedAssignedReports.length === 0}
                      title={isSuspended ? 'Disabled - Subscription Suspended' : ''}
                    >
                      Remove {selectedAssignedReports.length} Selected Report(s)
                    </button>
                    <span style={{ color: '#6c757d' }}>
                      {selectedAssignedReports.length} of {assignedReports.length} reports selected
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            className="btn btn-secondary"
            onClick={onClose}
            style={{ marginLeft: '1rem' }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Confirmation Modals */}
      {showDeleteConfirm && (
        <ConfirmationModal
          title="Delete User"
          message={assignedReports.length > 0 
            ? `Are you sure you want to delete user "${userName}"?\n\nThis user has ${assignedReports.length} report assignment(s) which will be removed.\n\nThis action cannot be undone.`
            : `Are you sure you want to delete user "${userName}"? This action cannot be undone.`}
          onConfirm={confirmDeleteUser}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="btn-danger"
        />
      )}

      {showNoLicenseAlert && (
        <ConfirmationModal
          title="No Available Licenses"
          message="No available licenses. Please deactivate another user license first."
          onConfirm={() => setShowNoLicenseAlert(false)}
          onCancel={() => setShowNoLicenseAlert(false)}
          confirmText="OK"
          cancelText=""
          confirmButtonClass="btn-primary"
        />
      )}

      {showNoReportsSelectedAlert && (
        <ConfirmationModal
          title="No Reports Selected"
          message={alertType === 'assign' 
            ? 'Please select at least one report to assign.'
            : 'Please select at least one report to remove.'}
          onConfirm={() => setShowNoReportsSelectedAlert(false)}
          onCancel={() => setShowNoReportsSelectedAlert(false)}
          confirmText="OK"
          cancelText=""
          confirmButtonClass="btn-primary"
        />
      )}
    </div>
  );
};

export default UserManagementPopup;